/**
 * * This component will render an icon based on the name passed as prop.
 * * The name is types in the TIconName type. This name is used to import the icon from the pds-icons folder, in this same folder.
 * * The icon is then rendered as a component.
 * * If you want to add a new icon, you need to add it to the pds-icons folder and export it from the index.ts file. Then add the name of the icon to the TIconName type.
 * * If an icon needs a specific color, you can pass it in the className prop, using tailwindcss classes. For example: className="co-text-red-500". After that, if the color is not being applied, you need to add it in the `fill` property of the `default-theme.js` file. This file is located in the `apps/cl-ccom-parisweb-store-front/src/themes` folder.
 */
import type { ReactElement, Ref } from 'react';
import { forwardRef } from 'react';

import * as IconModules from './icons';

type TProps = {
  icon: TIconName;
  width?: number;
  height?: number;
  solid?: boolean;
  className?: string;
};

export const PdsIcon = forwardRef(
  (
    { icon, width = 20, height = 20, className }: TProps,
    ref: Ref<SVGSVGElement>
  ): ReactElement => {
    const IconToRender = IconModules[icon];

    return (
      <IconToRender
        ref={ref}
        width={width}
        height={height}
        className={className}
      />
    );
  }
);

export type TIconName =
  | 'PdsInfoCircle'
  | 'PdsFilledInfoCircle'
  | 'PdsWarningCircle'
  | 'PdsFilledWarningCircle'
  | 'PdsErrorCircle'
  | 'PdsFilledErrorCircle'
  | 'PdsSuccessCircle'
  | 'PdsFilledSuccessCircle'
  | 'PdsPosventa'
  | 'PdsUndo'
  | 'PdsEdit'
  | 'PdsDelete'
  | 'PdsOutlineGiftCard'
  | 'PdsNovios';

export type TSvgIconProps = Pick<TProps, 'width' | 'height' | 'className'>;
